/**
 * EnhancedTimeline — multi-lane timeline of recorded actions on the video.
 *
 * Layers:
 *   - Background lanes per period (P1 / P2 / P3 / P4) with subtle dividers
 *   - Action markers as colored dots (color from getActionColor)
 *   - Current time cursor
 *   - Click-to-seek
 *   - Optional zoom (Ctrl/Cmd + scroll)
 *
 * The original timeline in EstadisticasNew.js stacked all markers on a
 * single line. This version groups them into 3 stacked lanes by category
 * (made / missed / others) so dense games stay readable.
 */

import React, { useRef, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';
import { getActionColor } from '../../utils/actionUtils';
import { formatTime } from '../../utils/timeUtils';

// Categorize an action into one of 3 lanes for visual grouping
function laneFor(actionCode) {
  if (!actionCode) return 2;
  if (actionCode.includes('MADE')) return 0;
  if (actionCode.includes('MISSED')) return 1;
  return 2;
}

const LANE_LABELS = ['Made', 'Missed', 'Other'];
const LANE_COUNT = 3;

export default function EnhancedTimeline({
  actions = [],
  videoDuration = 0,
  currentTime = 0,
  onSeek,
  periodOffsets = [],
  height = 110,
}) {
  const theme = useStatsTheme();
  const ref = useRef(null);
  const [zoom, setZoom] = useState(1);

  // Compute cumulative period boundaries (relative to video duration)
  const periodBoundaries = useMemo(() => {
    let cumulative = 0;
    const boundaries = [];
    for (const offset of periodOffsets) {
      cumulative += offset;
      boundaries.push(cumulative);
    }
    return boundaries;
  }, [periodOffsets]);

  const handleClick = useCallback((e) => {
    if (!ref.current || !videoDuration) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = x / rect.width / zoom;
    const time = ratio * videoDuration;
    if (typeof onSeek === 'function') onSeek(time);
  }, [videoDuration, onSeek, zoom]);

  const safeDuration = Math.max(1, videoDuration || 1);
  const cursorPercent = Math.min(100, (currentTime / safeDuration) * 100 * zoom);

  return (
    <Box
      sx={{
        backgroundColor: theme.surfaces.elevated,
        borderTop: `1px solid ${theme.surfaces.border}`,
        borderBottom: `1px solid ${theme.surfaces.border}`,
        py: 0.6,
        px: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: theme.text.secondary,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Timeline · {actions.length} actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.4 }}>
          <IconButton size="small" onClick={() => setZoom((z) => Math.max(1, z - 0.5))} sx={{ color: theme.text.secondary, width: 22, height: 22 }} aria-label="zoom out">
            <ZoomOutIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography sx={{ fontSize: 11, color: theme.text.secondary, minWidth: 28, textAlign: 'center' }}>{zoom}x</Typography>
          <IconButton size="small" onClick={() => setZoom((z) => Math.min(8, z + 0.5))} sx={{ color: theme.text.secondary, width: 22, height: 22 }} aria-label="zoom in">
            <ZoomInIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      <Box
        ref={ref}
        onClick={handleClick}
        sx={{
          position: 'relative',
          width: '100%',
          height,
          backgroundColor: theme.surfaces.base,
          borderRadius: `${theme.radius.md}px`,
          cursor: 'pointer',
          overflow: 'hidden',
          border: `1px solid ${theme.surfaces.border}`,
        }}
      >
        {/* Period dividers */}
        {periodBoundaries.map((boundary, idx) => {
          const leftPercent = (boundary / safeDuration) * 100 * zoom;
          if (leftPercent > 100) return null;
          return (
            <Box
              key={idx}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${leftPercent}%`,
                width: 1,
                backgroundColor: withAlpha(theme.text.muted, 0.4),
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  fontSize: 9,
                  fontWeight: 700,
                  color: theme.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                P{idx + 2}
              </Box>
            </Box>
          );
        })}

        {/* Lane backgrounds + labels */}
        {Array.from({ length: LANE_COUNT }).map((_, laneIdx) => {
          const laneHeight = (height - 8) / LANE_COUNT;
          return (
            <Box
              key={laneIdx}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 4 + laneIdx * laneHeight,
                height: laneHeight,
                borderBottom: laneIdx < LANE_COUNT - 1 ? `1px dashed ${theme.surfaces.divider}` : 'none',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  position: 'absolute',
                  left: 4,
                  fontSize: 8,
                  fontWeight: 700,
                  color: withAlpha(theme.text.muted, 0.7),
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  pointerEvents: 'none',
                }}
              >
                {LANE_LABELS[laneIdx]}
              </Typography>
            </Box>
          );
        })}

        {/* Action markers */}
        {actions.map((action, idx) => {
          const ts = action.videoTimestamp ?? action.timeSec ?? 0;
          const leftPercent = (ts / safeDuration) * 100 * zoom;
          if (leftPercent < 0 || leftPercent > 100) return null;
          const lane = laneFor(action.actionCode || action.action);
          const laneHeight = (height - 8) / LANE_COUNT;
          const top = 4 + lane * laneHeight + laneHeight / 2;
          const color = getActionColor(action.actionCode || action.action) || theme.actionColors.neutral;

          return (
            <Tooltip key={idx} title={`${action.playerName}: ${action.action} · ${formatTime(Math.floor(ts))}`}>
              <Box
                sx={{
                  position: 'absolute',
                  left: `${leftPercent}%`,
                  top,
                  transform: 'translate(-50%, -50%)',
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  backgroundColor: color,
                  border: `1.5px solid ${theme.surfaces.base}`,
                  boxShadow: `0 0 6px ${withAlpha(color, 0.6)}`,
                  zIndex: 2,
                  transition: `transform ${theme.motion.fast}`,
                  '&:hover': { transform: 'translate(-50%, -50%) scale(1.5)' },
                }}
              />
            </Tooltip>
          );
        })}

        {/* Current time cursor */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${cursorPercent}%`,
            width: 2,
            backgroundColor: theme.accent,
            boxShadow: `0 0 10px ${withAlpha(theme.accent, 0.7)}`,
            zIndex: 3,
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Box>
  );
}
